import React, { useState } from 'react';
import AnalysisPageLayout from '../../components/aesthetic/AnalysisPageLayout';
import ImageUpload, { validateImageFile } from '../../components/aesthetic/ImageUpload';
import MetricCard from '../../components/aesthetic/MetricCard';
import BeforeAfterComparison from '../../components/aesthetic/BeforeAfterComparison';
import LoadingOverlay from '../../components/aesthetic/LoadingOverlay';
import ErrorBanner from '../../components/aesthetic/ErrorBanner';
import EmptyState from '../../components/aesthetic/EmptyState';
import { analyzeBreastSurgery } from '../../services/aestheticApi';
import { readFileAsDataUrl, toDataUri } from '../../utils/imageUtils';

const SURGERY_TYPES = [
  { value: 'augmentation', label: 'Augmentation' },
  { value: 'reduction', label: 'Reduction' },
  { value: 'lift', label: 'Lift' },
  { value: 'reconstruction', label: 'Reconstruction' },
];

function buildClinicalSummary(results) {
  const symmetry = results.symmetryScore?.toFixed?.(1) ?? results.symmetryScore;
  const volumeDiff = results.volumeDifferencePercent?.toFixed?.(1) ?? results.volumeDifferencePercent;
  const surgery = results.surgeryType || 'procedure';

  return `Post-${surgery} analysis indicates a symmetry score of ${symmetry}%, with left breast volume at ${results.leftVolume} cc and right at ${results.rightVolume} cc. Volume difference is ${volumeDiff}%. Nipple height difference measures ${results.nippleHeightDifference} mm and inframammary fold difference is ${results.foldDifference} mm. Overall findings suggest ${Number(symmetry) >= 95 ? 'excellent' : Number(symmetry) >= 90 ? 'good' : 'moderate'} post-operative symmetry.`;
}

function BreastAnalysis() {
  const [frontImage, setFrontImage] = useState(null);
  const [beforeImage, setBeforeImage] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [beforePreview, setBeforePreview] = useState(null);
  const [surgeryType, setSurgeryType] = useState('augmentation');
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleImageChange = async (field, file) => {
    setFieldErrors((prev) => ({ ...prev, [field]: null }));
    setError('');

    if (!file) {
      if (field === 'front') {
        setFrontImage(null);
        setFrontPreview(null);
      } else {
        setBeforeImage(null);
        setBeforePreview(null);
      }
      return;
    }

    const validationError = validateImageFile(file);
    if (validationError) {
      setFieldErrors((prev) => ({ ...prev, [field]: validationError }));
      return;
    }

    const preview = await readFileAsDataUrl(file);
    if (field === 'front') {
      setFrontImage(file);
      setFrontPreview(preview);
    } else {
      setBeforeImage(file);
      setBeforePreview(preview);
    }
  };

  const handleAnalyze = async () => {
    const errors = {};
    if (!frontImage) errors.front = 'After image is required.';
    if (!beforeImage) errors.before = 'Before image is required.';
    if (!surgeryType) errors.surgeryType = 'Surgery type is required.';

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setError('Please complete all required fields before analyzing.');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('front_image', frontImage);
      formData.append('before_image', beforeImage);
      formData.append('surgery_type', surgeryType);

      const data = await analyzeBreastSurgery(formData);

      if (!data?.success) {
        throw new Error(data?.message || 'Analysis failed. Please try again.');
      }

      setResults(data);
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Unable to analyze images.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const annotatedImage = results ? toDataUri(results.annotatedImage) : null;

  return (
    <AnalysisPageLayout
      title="Breast Analysis"
      subtitle="Upload front and before images to evaluate post-surgical symmetry and volume metrics."
    >
      {loading && <LoadingOverlay message="Running breast surgery analytics…" />}

      <section className="aesthetic-section">
        <h2 className="aesthetic-section-title">Upload & Configure</h2>
        <ErrorBanner message={error} />
        <div className="upload-form-grid">
          <ImageUpload
            label="After Image"
            preview={frontPreview}
            onChange={(file) => handleImageChange('front', file)}
            error={fieldErrors.front}
          />
          <ImageUpload
            label="Before Image"
            preview={beforePreview}
            onChange={(file) => handleImageChange('before', file)}
            error={fieldErrors.before}
          />
          <div className="image-upload-field">
            <label className="image-upload-label" htmlFor="surgery-type">
              Surgery Type
            </label>
            <select
              id="surgery-type"
              className="form-select"
              value={surgeryType}
              onChange={(e) => setSurgeryType(e.target.value)}
            >
              {SURGERY_TYPES.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {fieldErrors.surgeryType && (
              <span className="image-upload-error">{fieldErrors.surgeryType}</span>
            )}
          </div>
        </div>
        <div className="upload-form-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleAnalyze}
            disabled={loading}
          >
            Analyze
          </button>
        </div>
      </section>

      {!results && !loading && (
        <section className="aesthetic-section">
          <EmptyState
            title="No analysis results yet"
            description="Upload a After image, before image, and select a surgery type, then click Analyze to generate symmetry and volume metrics."
          />
        </section>
      )}

      {results && (
        <>
          <section className="aesthetic-section">
            <h2 className="aesthetic-section-title">Analytics Dashboard</h2>
            <div className="metrics-grid">
              <MetricCard label="Symmetry Score" value={results.symmetryScore} unit="%" highlight />
              <MetricCard label="Left Breast Volume" value={results.leftVolume} unit="cc" />
              <MetricCard label="Right Breast Volume" value={results.rightVolume} unit="cc" />
              <MetricCard label="Volume Difference" value={results.volumeDifferencePercent} unit="%" />
              <MetricCard label="Nipple Height Diff." value={results.nippleHeightDifference} unit="mm" />
              <MetricCard label="Fold Difference" value={results.foldDifference} unit="mm" />
            </div>
          </section>

          {annotatedImage && (
            <section className="aesthetic-section">
              <h2 className="aesthetic-section-title">Annotated Analysis</h2>
              <div className="visualization-grid">
                <div className="visualization-card">
                  <div className="visualization-card-header">Annotated Image</div>
                  <div className="visualization-card-body">
                    <img src={annotatedImage} alt="Annotated breast analysis" />
                  </div>
                </div>
              </div>
            </section>
          )}

          <section className="aesthetic-section">
            <h2 className="aesthetic-section-title">Clinical Report Summary</h2>
            <p className="clinical-summary">{buildClinicalSummary(results)}</p>
          </section>

          <section className="aesthetic-section">
            <h2 className="aesthetic-section-title">Before / After Preview</h2>
            <BeforeAfterComparison
              beforeImage={beforePreview}
              afterImage={frontPreview}
              beforeLabel="Before"
              afterLabel="Front (Post)"
            />
          </section>

          <section className="aesthetic-section">
            <h2 className="aesthetic-section-title">Results Overview</h2>
            <div className="metrics-grid">
              <MetricCard label="Procedure" value={results.surgeryType} />
              <MetricCard label="Symmetry Score" value={results.symmetryScore} unit="%" highlight />
              <MetricCard label="Volume Balance" value={results.volumeDifferencePercent} unit="%" />
              <MetricCard label="Nipple Alignment" value={results.nippleHeightDifference} unit="mm" />
            </div>
          </section>
        </>
      )}
    </AnalysisPageLayout>
  );
}

export default BreastAnalysis;
